import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Icon } from '@repo/ui';

export default function ClioCallbackPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing Clio authorization...');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get URL parameters
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        // Handle OAuth errors from Clio
        if (error) {
          setStatus('error');
          setMessage(errorDescription || `Authorization failed: ${error}`);

          // Send error message to parent window
          window.opener?.postMessage(
            {
              type: 'CLIO_AUTH_ERROR',
              error: errorDescription || error,
            },
            window.location.origin
          );

          // Close window after delay
          setTimeout(() => {
            window.close();
          }, 3000);
          return;
        }

        // Handle missing required parameters
        if (!code || !state) {
          setStatus('error');
          setMessage('Invalid authorization response - missing required parameters');

          window.opener?.postMessage(
            {
              type: 'CLIO_AUTH_ERROR',
              error: 'Invalid authorization response',
            },
            window.location.origin
          );

          setTimeout(() => {
            window.close();
          }, 3000);
          return;
        }

        // Success - send the code and state back to parent window
        setStatus('success');
        setMessage('Authorization successful! Closing window...');

        window.opener?.postMessage(
          {
            type: 'CLIO_AUTH_SUCCESS',
            code,
            state,
          },
          window.location.origin
        );

        // Close window after showing success
        setTimeout(() => {
          window.close();
        }, 2000);
      } catch (error) {
        console.error('Clio OAuth callback error:', error);
        setStatus('error');
        setMessage(error instanceof Error ? error.message : 'Authorization failed');

        window.opener?.postMessage(
          {
            type: 'CLIO_AUTH_ERROR',
            error: error instanceof Error ? error.message : 'Authorization failed',
          },
          window.location.origin
        );

        setTimeout(() => {
          window.close();
        }, 3000);
      }
    };

    handleCallback();
  }, [searchParams]);

  const getStatusIcon = () => {
    switch (status) {
      case 'processing':
        return <Icon name="HiRefresh" size="2xl" className="text-xtnd-blue animate-spin" />;
      case 'success':
        return <Icon name="HiCheckCircle" size="2xl" className="text-green-500" />;
      case 'error':
        return <Icon name="HiXCircle" size="2xl" className="text-red-500" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'processing':
        return 'text-xtnd-blue';
      case 'success':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-xtnd-light to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-xtnd-blue rounded-full mb-4">
            <svg
              className="h-8 w-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-xtnd-dark mb-2">Clio Integration</h1>
          <p className="text-gray-600 text-sm">Connecting to your Clio account</p>
        </div>

        {/* Status Content */}
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="flex justify-center mb-6">{getStatusIcon()}</div>

          <h2 className={`text-xl font-semibold mb-4 ${getStatusColor()}`}>
            {status === 'processing' && 'Processing Authorization'}
            {status === 'success' && 'Connection Successful!'}
            {status === 'error' && 'Connection Failed'}
          </h2>

          <p className="text-gray-600 mb-6">{message}</p>

          {status === 'processing' && (
            <div className="flex justify-center">
              <div className="animate-pulse flex space-x-1">
                <div className="w-2 h-2 bg-xtnd-blue rounded-full"></div>
                <div className="w-2 h-2 bg-xtnd-blue rounded-full animation-delay-200"></div>
                <div className="w-2 h-2 bg-xtnd-blue rounded-full animation-delay-400"></div>
              </div>
            </div>
          )}

          {status === 'success' && (
            <p className="text-sm text-green-600">This window will close automatically...</p>
          )}

          {status === 'error' && (
            <div className="space-y-2">
              <p className="text-sm text-gray-500">This window will close automatically.</p>
              <button
                onClick={() => window.close()}
                className="text-sm text-xtnd-blue hover:text-xtnd-dark underline"
              >
                Close window now
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
